/**
 * Phase 5F — Student Learning Plan section.
 * One umbrella view: subjects · tutors · weekly grid · next lesson · payment · timetable.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Clock, Users, FileDown, ExternalLink, CheckCircle2,
  AlertTriangle, Calendar, GraduationCap, CreditCard, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const StatusPill = ({ status }) => {
  const cls = status === 'paid' ? 'bg-emerald-100 text-emerald-800'
    : status === 'partial' ? 'bg-amber-100 text-amber-800'
    : 'bg-rose-100 text-rose-800';
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid="plan-payment-status">
      {status || 'unpaid'}
    </span>
  );
};

const StudentLearningPlanSection = ({ onPayPlan }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await ax.get('/api/me/student/learning-plan');
        if (live) setPlan(r.data?.plan || null);
      } catch (_e) { /* silent */ }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);

  const downloadPdf = async () => {
    try {
      const r = await ax.get('/api/me/student/learning-plan/timetable.pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `winhouse-learning-plan.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error('Could not download timetable');
    }
  };

  if (loading) return null;
  if (!plan) return null;

  const subjects = plan.subjects || [];
  const tutors = plan.tutors || [];
  const grid = plan.weekly_timetable || {};
  const next = plan.next_lesson;
  const totalAud = (plan.total_amount_aud_cents || 0) / 100;
  const sessionsTotal = subjects.reduce((acc, s) => acc + (s.num_sessions || 0), 0);
  const timeSlots = Object.keys(grid).sort();
  const remaining = plan.sessions_remaining_by_series || {};
  const completedBookings = (plan.bookings || []).filter(b => b.status === 'completed').length;
  const remainingTotal = Object.values(remaining).reduce((a, b) => a + (b || 0), 0);

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm" data-testid="student-learning-plan-section">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">Your Learning Plan</div>
          <h2 className="text-lg font-bold text-gray-900 mt-0.5" data-testid="plan-name">{plan.plan_name}</h2>
          <div className="text-[11px] text-gray-500 mt-1">
            {subjects.length} subjects · {tutors.length} tutors · {sessionsTotal} sessions · AUD {totalAud.toFixed(2)}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill status={plan.payment_status} />
          {plan.payment_status !== 'paid' && plan.payment_link && (
            <a href={plan.payment_link} target="_blank" rel="noopener noreferrer"
              className="h-9 px-4 rounded-full bg-amber-500 hover:bg-amber-600 text-black text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
              data-testid="plan-pay-now">
              <CreditCard className="h-3 w-3" /> Pay AUD {totalAud.toFixed(2)}
            </a>
          )}
          {plan.payment_status !== 'paid' && !plan.payment_link && (
            <button onClick={() => onPayPlan && onPayPlan(plan)}
              className="h-9 px-4 rounded-full bg-amber-500 hover:bg-amber-600 text-black text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
              data-testid="plan-pay-fallback">
              <CreditCard className="h-3 w-3" /> Pay AUD {totalAud.toFixed(2)}
            </button>
          )}
          <button onClick={downloadPdf}
            className="h-9 px-4 rounded-full border border-gray-200 hover:bg-gray-50 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
            data-testid="plan-pdf-download">
            <FileDown className="h-3 w-3" /> Timetable PDF
          </button>
        </div>
      </div>

      {/* Quick stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Stat label="Subjects" value={subjects.length} icon={BookOpen} testid="lp-stat-subjects" />
        <Stat label="Tutors" value={tutors.length} icon={Users} testid="lp-stat-tutors" />
        <Stat label="Completed" value={completedBookings} icon={CheckCircle2} testid="lp-stat-completed" />
        <Stat label="Remaining" value={remainingTotal} icon={Clock} testid="lp-stat-remaining" />
      </div>

      {/* Next lesson banner */}
      {next && (
        <div className="bg-gradient-to-br from-red-50 to-amber-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-next-lesson">
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="h-5 w-5 text-red-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[2px] text-red-700 font-bold">Next lesson</div>
              <div className="text-sm font-bold text-gray-900 truncate">{next.subject || 'Class'}</div>
              <div className="text-[11px] text-gray-600 truncate">
                with {next.tutor_name} · {new Date(next.start_time).toLocaleString()}
              </div>
            </div>
          </div>
          {next.join_path && (
            <a href={next.join_path} target="_blank" rel="noopener noreferrer"
              className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 shrink-0"
              data-testid="plan-next-join">
              <ExternalLink className="h-3 w-3" /> Join
            </a>
          )}
        </div>
      )}

      {/* Tutors */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold mb-2 flex items-center gap-1.5">
          <Users className="h-3 w-3" /> Your tutors
        </div>
        <div className="flex flex-wrap gap-2" data-testid="plan-tutors">
          {tutors.length === 0 && <span className="text-[11px] text-gray-400 italic">No tutors assigned yet.</span>}
          {tutors.map((t) => (
            <span key={`${t.tutor_email}-${t.subject}`} className="bg-gray-50 border border-gray-200 rounded-full px-3 py-1 text-[11px] flex items-center gap-1.5" data-testid={`plan-tutor-${t.tutor_email}`}>
              <GraduationCap className="h-3 w-3 text-red-700" />
              <strong>{t.tutor_name}</strong>
              <span className="text-gray-500">· {t.subject}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Weekly timetable grid (school-style) */}
      {timeSlots.length > 0 ? (
        <div className="overflow-x-auto mb-4" data-testid="plan-weekly-grid">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr>
                <th className="bg-black text-[#D3D86F] p-2 text-[10px] font-bold uppercase tracking-wider w-16 sticky left-0">Time</th>
                {DAYS.map(d => (
                  <th key={d} className="bg-black text-[#D3D86F] p-2 text-[10px] font-bold uppercase tracking-wider">{d.slice(0, 3)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(t => (
                <tr key={t}>
                  <td className="bg-black text-[#D3D86F] p-2 text-center font-bold text-[11px] sticky left-0">{t}</td>
                  {DAYS.map(d => {
                    const cell = grid[t]?.[d];
                    return (
                      <td key={d} className={`border border-gray-200 p-1.5 align-top h-14 ${cell ? 'bg-[#FAF9F6]' : 'bg-white'}`} data-testid={cell ? `grid-${d}-${t}` : undefined}>
                        {cell && (
                          <div className="text-center">
                            <div className="font-bold text-gray-900 truncate">{cell.subject}</div>
                            <div className="text-[9px] text-gray-500 truncate">{cell.tutor_name}</div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-[11px] text-gray-400 italic mb-4 flex items-center gap-2">
          <AlertTriangle className="h-3 w-3" /> Your timetable will appear once sessions are scheduled.
        </div>
      )}

      {/* Subject breakdown */}
      <div className="border-t border-gray-100 pt-4">
        <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold mb-2 flex items-center gap-1.5">
          <Calendar className="h-3 w-3" /> Subject breakdown
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-[10px] uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 pr-3">Subject</th>
                <th className="text-left py-2 pr-3">Tutor</th>
                <th className="text-left py-2 pr-3">Day</th>
                <th className="text-left py-2 pr-3">Time</th>
                <th className="text-left py-2 pr-3">Sessions</th>
                <th className="text-left py-2 pr-3">Package</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map(s => (
                <tr key={s.series_id} className="border-b last:border-b-0" data-testid={`plan-subject-row-${s.series_id}`}>
                  <td className="py-2 pr-3 font-bold text-gray-900">{s.subject}</td>
                  <td className="py-2 pr-3 text-gray-700">{s.tutor_name || s.tutor_email?.split('@')[0]}</td>
                  <td className="py-2 pr-3 text-gray-700">{s.day_of_week}</td>
                  <td className="py-2 pr-3 text-gray-700">{s.start_time}</td>
                  <td className="py-2 pr-3 text-gray-700">{remaining[s.series_id] ?? s.num_sessions} / {s.num_sessions}</td>
                  <td className="py-2 pr-3 text-gray-700">{(s.package_type || 'individual').replace('_', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-gray-50 border border-gray-100 rounded-xl p-3" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-[2px] text-gray-500 font-bold flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </div>
    <div className="text-xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

export default StudentLearningPlanSection;
